import React from 'react';
import Link from 'next/link';
import BetaBadge from '@components/ask-ffh/BetaBadge';
import Icon from '@components/atoms/icon';
import { ASK_FFH_PATH } from '@lib/ask-ffh/config';
import type { NavBarType } from '@lib/constants';

/** Ask FFH is shown only to members the backend has let into the beta. */
export const isHiddenAskFfhLink = (
  item: Pick<NavBarType, 'url'>,
  isAskFfhEligible: boolean
): boolean => item.url === ASK_FFH_PATH && !isAskFfhEligible;

export const generateDesktopNavLinks = ({
  navData,
  isUserAuthenticated,
  status,
  isCurrentPath,
  path,
  subMenuActiveLevel,
  setSubMenuActiveLevel
}) => {
  return navData.map((heading) => {
    if (!isUserAuthenticated(status) && heading.isHiddenForGuests) {
      return;
    }
    const [, ...subMenuLinks] = navData.filter(
      (item) => item.level.split('.')[0] === subMenuActiveLevel
    );

    if (heading.isTopLevel) {
      return (
        <div
          key={`${heading.level}-${heading.name}`}
          onMouseEnter={() => setSubMenuActiveLevel(heading.level)}
          onMouseLeave={() => setSubMenuActiveLevel(undefined)}
          className={`cursor-pointer inline-flex font-bold relative relative flex hover:bg-primary-100 hover:text-primary-800 rounded-lg items-center text-sm font-bold font-open-sans p-px lg:p-2 mr-1 text-white ${
            isCurrentPath(path, heading) ? 'mx-1 bg-primary-600' : ''
          }`}
        >
          <Link
            href={heading.url}
            data-cy={heading?.dataCy}
            target={`${heading.isOpenInNewWindow ? '_blank' : '_self'}`}
            className="font-bold flex items-center"
          >
            {heading.name}
            {heading.isBeta && <BetaBadge className="ml-1.5" />}
            {heading.isOpenInNewWindow && (
              <Icon name="ExternalLink" className="ml-1 w-3 h-3" />
            )}
            {heading.hasSubMenu && (
              <Icon
                name="ChevronDown"
                className={`transition-transform ml-1 w-3 h-3 ${
                  subMenuActiveLevel === heading.level ? 'rotate-180' : ''
                }`}
              />
            )}
          </Link>
          {heading.hasSubMenu && subMenuActiveLevel === heading.level && (
            <div className="pt-4 bg-transparent absolute z-20 top-8 left-0">
              <div
                className={`${
                  heading?.isGridView
                    ? 'grid grid-cols-min-max-content gap-x-4'
                    : ''
                } p-8 pt-4 ml-12 shadow-300 space-y-3 rounded-lg bg-neutral-50`}
              >
                {subMenuLinks?.map((item, index) => {
                  if (!item.url) {
                    return (
                      <p
                        key={`${item.level}-${item.name}`}
                        className={`${
                          heading?.isGridView ? 'col-span-2' : ''
                        } uppercase pt-4 text-xs font-bold text-open-sans text-neutral-600`}
                      >
                        {item.name}
                      </p>
                    );
                  }
                  return (
                    <div key={`${item.name}-${index}`} className="col-span-1">
                      <Link
                        href={item.url}
                        target={`${
                          item.isOpenInNewWindow ? '_blank' : '_self'
                        }`}
                        rel={`${item.isOpenInNewWindow ? 'noreferrer' : ''}`}
                        className="flex items-center text-neutral-800 items-center cursor-pointer hover:text-neutral-600 text-base"
                      >
                        {item.name}{' '}
                        {item.isOpenInNewWindow && (
                          <Icon name="ExternalLink" className="ml-1 w-3 h-3" />
                        )}
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }
  });
};

export const generateMobileNavLinks = ({
  navData,
  isUserAuthenticated,
  status,
  isCurrentPath,
  path
}) =>
  navData.map((heading) => {
    if (!isUserAuthenticated(status) && heading.isHiddenForGuests) {
      return;
    }

    if (heading.isTopLevel) {
      return (
        <Link
          key={heading.level}
          href={heading.url}
          className={`text-xs font-semibold px-4 tracking-wider
            ${isCurrentPath(path, heading) ? 'active' : ''}`}
          target={`${heading.isOpenInNewWindow ? '_blank' : '_self'}`}
        >
          {heading.shortName || heading.name}
          {heading.isBeta && <BetaBadge className="ml-1" />}
        </Link>
      );
    }
  });

export const insertGameWeek = (str: string, nextGameweek: number | null) => {
  if (nextGameweek == null) {
    return 'FPL GW1';
  }
  const updatedStr = str.replace('$', String(nextGameweek));
  return updatedStr === 'GW1 Tips' ? 'GW1 Ultimate Guide' : updatedStr;
};
